import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import { TooltipLabel } from "../../components/Tooltips";

/**
 * One quality axis, as a multi-select.
 *
 * `null` is a real option rather than an absence — it stands for "no value
 * recorded", which is the majority of the catalogue for stars and review scores.
 * Offering it explicitly is what stops deselecting one value from also, invisibly,
 * dropping every competitor the provider said nothing about.
 *
 * ⚠️ Built on `CustomAutocomplete` rather than `CustomSelect`, which also does
 * `multiple`: `CustomSelect` derives each menu label from the option value and
 * title-cases it, so the translated unknown label would render as "Non Renseigné" in
 * French and "Sin Datos" in Spanish. `getOptionLabel` keeps the label the
 * translation gave it.
 *
 * `tooltip` is a translation *key*, not a sentence: `TooltipLabel` runs it through
 * `intl` itself. Given one, the label grows the same info icon the rest of the app
 * uses — worth having on the two axes whose names do not say where the number came
 * from, since `interest_rating` is ours and `rating` is Google's.
 */
export type FilterOption = { value: number | null; label: string };

const QualityFilter = <T extends number | null>({
  label,
  tooltip,
  options,
  selected,
  onChange,
}: {
  label: string;
  tooltip?: string;
  options: FilterOption[];
  selected: T[];
  onChange: (next: T[]) => void;
}) => (
  <CustomAutocomplete
    getOptionKey={(option) => String(option.value)}
    label={
      tooltip ? <TooltipLabel label={label} tooltipLabel={tooltip} /> : label
    }
    options={options}
    // Mapped back to option objects every render rather than held as state: the
    // selection lives in the page, which is what the query key reads.
    value={options.filter((option) => selected.includes(option.value as T))}
    multiple
    disableCloseOnSelect
    disableClearable
    limitTags={3}
    size="small"
    getOptionLabel={(option: FilterOption) => option.label}
    isOptionEqualToValue={(option: FilterOption, chosen: FilterOption) =>
      option.value === chosen.value
    }
    onChange={(_event: unknown, next: FilterOption[]) =>
      onChange(next.map((option) => option.value as T))
    }
  />
);

export default QualityFilter;
